import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Plus, RefreshCw, Trash2, Pencil } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { DataTable, createSortableHeader } from "../../../components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { DeleteOperationDialog } from "../components/delete-operation-dialog";
import { OperationFormDialog } from "../components/operation-form-dialog";
import { TemplateFormDialog } from "../components/template-form-dialog";
import { useOperations } from "../hooks/use-operations-queries";
import { useTemplates, useDeleteTemplate } from "../hooks/use-template-queries";
import { Operation, OpsTemplate } from "../types";

export default function OperationsManagementPage() {
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(
    null
  );
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<OpsTemplate | null>(null);

  const {
    data: operations = [],
    isLoading,
    refetch,
    isRefetching,
  } = useOperations();

  const { data: templates = [] } = useTemplates();
  const deleteTemplate = useDeleteTemplate();

  const handleEditOperation = (operation: Operation) => {
    setSelectedOperation(operation);
    setIsFormDialogOpen(true);
  };

  const handleDeleteOperation = (operation: Operation) => {
    setSelectedOperation(operation);
    setIsDeleteDialogOpen(true);
  };

  const handleAddOperation = () => {
    setSelectedOperation(null);
    setIsFormDialogOpen(true);
  };

  const handleFormClose = () => {
    setIsFormDialogOpen(false);
    setSelectedOperation(null);
  };

  const handleDeleteClose = () => {
    setIsDeleteDialogOpen(false);
    setSelectedOperation(null);
  };

  const handleAddTemplate = () => {
    setSelectedTemplate(null);
    setIsTemplateDialogOpen(true);
  };

  const handleEditTemplate = (template: OpsTemplate) => {
    setSelectedTemplate(template);
    setIsTemplateDialogOpen(true);
  };

  const handleTemplateClose = () => {
    setIsTemplateDialogOpen(false);
    setSelectedTemplate(null);
  };

  const columns: ColumnDef<Operation>[] = [
    {
      accessorKey: "ops",
      header: createSortableHeader("Operação"),
      cell: ({ row }) => {
        const operation = row.original;
        return (
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              style={{
                borderColor: operation.color,
                color: operation.color,
              }}
            >
              {operation.displayName}
            </Badge>
            <span className="text-sm text-muted-foreground">
              ({operation.ops})
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "db_name",
      header: createSortableHeader("Banco de Dados"),
      cell: ({ row }) => (
        <div className="font-mono text-sm">{row.original.db_name}</div>
      ),
    },
    {
      accessorKey: "screens",
      header: "Telas",
      enableSorting: false,
      cell: ({ row }) => {
        const screenCount = row.original.screens.length;
        return (
          <div className="text-sm">
            {screenCount > 0 ? (
              <Badge variant="secondary">{screenCount} telas</Badge>
            ) : (
              <span className="text-muted-foreground">Nenhuma tela</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "uploads",
      header: "Uploads",
      enableSorting: false,
      cell: ({ row }) => {
        const uploadCount = row.original.uploads.length;
        return (
          <div className="text-sm">
            {uploadCount > 0 ? (
              <Badge variant="secondary">{uploadCount} uploads config</Badge>
            ) : (
              <span className="text-muted-foreground">Nenhum upload</span>
            )}
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const operation = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir Menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleEditOperation(operation)}>
                Editar operação
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDeleteOperation(operation)}
                className="text-red-600"
                disabled={operation.ops === "gestor"}
              >
                Excluir operação
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="w-full py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Gerenciar Operações</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie operações, telas e configurações de upload
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={handleAddOperation}>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Operação
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <DataTable
          columns={columns}
          data={operations}
          searchableColumns={["ops"]}
          searchPlaceholder="Buscar por operação..."
        />
      </div>

      <OperationFormDialog
        operation={selectedOperation}
        isOpen={isFormDialogOpen}
        onClose={handleFormClose}
      />

      <DeleteOperationDialog
        operation={selectedOperation}
        isOpen={isDeleteDialogOpen}
        onClose={handleDeleteClose}
      />

      {/* ─── Templates ────────────────────────────────────────────────────── */}

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">Gerenciar Templates</h2>
            <p className="text-sm text-muted-foreground">
              Templates reutilizáveis de telas e uploads para operações
            </p>
          </div>
          <Button onClick={handleAddTemplate} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Template
          </Button>
        </div>

        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum template cadastrado
          </p>
        ) : (
          <div className="rounded-md border divide-y">
            {templates.map((tpl: OpsTemplate) => (
              <div
                key={tpl.id}
                className="flex items-center justify-between px-4 py-3"
              >
                <div>
                  <p className="font-medium text-sm">{tpl.name}</p>
                  {tpl.description && (
                    <p className="text-xs text-muted-foreground">
                      {tpl.description}
                    </p>
                  )}
                  <div className="flex gap-2 mt-1">
                    {tpl.screens.length > 0 && (
                      <Badge variant="secondary">
                        {tpl.screens.length} telas
                      </Badge>
                    )}
                    {tpl.uploads.length > 0 && (
                      <Badge variant="secondary">
                        {tpl.uploads.length} uploads
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditTemplate(tpl)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteTemplate.mutateAsync(tpl.id)}
                    disabled={deleteTemplate.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TemplateFormDialog
        template={selectedTemplate}
        isOpen={isTemplateDialogOpen}
        onClose={handleTemplateClose}
      />
    </div>
  );
}
